import { GlobalApplicationState } from 'globalApplicationState';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TopicGroup, emptyTopicDetails } from 'modules/topics';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import { Button, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { CloseIcon } from 'tinacms';
import TopicCreationBody from './common/topicCreationBody';
import { createTopic, getTopicGroups, topicSlice } from 'modules/topics/reducer';
import { ThunkDispatch } from '@reduxjs/toolkit';

const TopicCreation: React.FunctionComponent<ComponentProps> = (props) => {
    const [topicCreationInput, setTopicCreationInput] = React.useState(emptyTopicDetails);
    const [isError, setIsError] = React.useState(false);

    const {
        topicGroups,
        isUpdating: isSaving,
    } = useSelector((state: GlobalApplicationState) => state.topics);
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

    const isNotFilled = (): boolean =>
        topicCreationInput.groupId?.trim().length === 0 ||
        topicCreationInput.name.trim().length === 0;

    const handleClose = () => {
        setTopicCreationInput(emptyTopicDetails);
        dispatch(topicSlice.actions.SET_ERR_MSG(""));
        props.onClose();
    };

    const handleCreation = async () => {
        if (isSaving) return;
        const isSuccessful = await dispatch(createTopic(topicCreationInput)).unwrap();

        if (isSuccessful) {
            dispatch(getTopicGroups());
            props.onClose();
        }
    };

    const getDialogHeader = (): JSX.Element =>
        <DialogTitle className="audience-dialog-title">
            Create New Topic
            <IconButton onClick={handleClose} size="medium">
                <CloseIcon />
            </IconButton>
        </DialogTitle>;

    const getDialogFooter = (): JSX.Element =>
        <DialogActions>
            <Button variant="text" onClick={handleClose}>Cancel</Button>
            <Button
                variant="contained"
                color="primary"
                disabled={isError || isNotFilled()}
                onClick={handleCreation}
            >
                Create
            </Button>
        </DialogActions>;

    return (
        <Dialog
            open={props.show}
            maxWidth={false}
            scroll="paper"
            onClose={handleClose}
            classes={{ paper: "audience-dialog" }}
        >
            {getDialogHeader()}
            <DialogContent className={"dialog-content"}>
                <TopicCreationBody
                    topicGroups={topicGroups}
                    input={topicCreationInput}
                    setInput={setTopicCreationInput}
                    setIsError={setIsError}
                    defaultGroup={props.defaultGroup ?
                        {
                            label: props.defaultGroup.name,
                            id: props.defaultGroup.id
                        } : undefined
                    }
                />
            </DialogContent>
            {getDialogFooter()}
        </Dialog>
    );
};

interface ComponentProps {
    show: boolean,
    onClose: () => void,
    defaultGroup?: TopicGroup;
}

export default TopicCreation;